import React from 'react';
import { useQuery } from 'react-query';

import classNames from 'classnames';
import styled from 'styled-components';

import { WIDGET_TYPES } from 'common/Widgets/constants';
import { DATA_RANGE_TYPE } from 'common/Widgets/Widget/constants';
import LoaderRibbed from 'components/lib/LoaderRibbed';
import { usePublicationContext } from 'components/Publication/PublicationContext';
import { useSalesBattlecardContext } from 'components/SalesBattlecard/SalesBattlecardContext';
import { useCommunityViewContext } from 'components/View/CommunityViewContext';
import { useWidgetDataFetcher } from 'hooks/useWidgetDataFetcher';

import GroupedInsightCard from './GroupedInsightCard';
import InsightCard from './InsightCard';
import { useWidgetContext } from '../Widget/WidgetContext';

const Container = styled.div`
  display: flex;
  flex-flow: column;
  height: 100%;
  overflow: hidden;
  margin-top: 20px;
  gap: 20px;
  position: relative;

  &.modal-preview {
    overflow: unset;
    width: 1000px;
  }
`;

const CardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex: 1;
  overflow-y: auto;

  &.modal-preview {
    overflow-y: unset;
  }
`;

const Row = styled.div`
  display: flex;
  flex-flow: row;
  gap: 20px;
`;

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  min-height: 200px;
`;

export const AIInsightsWidgetContent = () => {
  const { communityId, competitorId } = useCommunityViewContext();
  const { selectedCompetitorId: battlecardCompetitorId } = useSalesBattlecardContext();
  const { isModalPreview, filters: widgetFilters = { competitors: [] } } = useWidgetContext();
  const { fetchWidgetData, snapshotUuids } = useWidgetDataFetcher();
  const { publication } = usePublicationContext();

  // If in Sales Battlecard, use the tab-selected competitor; if on competitor page, use competitorId; otherwise use communityId
  const filterCompetitors = widgetFilters?.competitors ?? [];
  const effectiveCompetitorId =
    battlecardCompetitorId || competitorId || (filterCompetitors.length === 1 ? filterCompetitors[0] : null);
  const selectedCommunityId = effectiveCompetitorId || communityId;

  const { data: aiInsightsData, isFetching } = useQuery({
    queryKey: [
      'ai-insights',
      communityId,
      selectedCommunityId,
      battlecardCompetitorId,
      snapshotUuids,
      publication?.hash,
    ],
    queryFn: () =>
      fetchWidgetData(communityId, WIDGET_TYPES.AI_INSIGHTS, DATA_RANGE_TYPE.NOW, {
        filters: {
          competitors: effectiveCompetitorId ? [effectiveCompetitorId] : [],
          care_types: [],
          room_types: [],
          care_levels: [],
          review_sources: [],
          review_categories: [],
          swot_categories: [],
        },
        ...(publication?.hash ? { public_hash: publication.hash } : {}),
      }),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    keepPreviousData: true,
    enabled: communityId > 0 && snapshotUuids !== null,
  });

  const insightsData = aiInsightsData || null;

  return (
    <Container className={classNames({ 'modal-preview': isModalPreview })} data-testid="ai-insights-widget">
      {isFetching ? (
        <LoaderContainer>
          <LoaderRibbed size="small" />
        </LoaderContainer>
      ) : (
        <CardsContainer className={classNames({ 'modal-preview': isModalPreview, 'mini-scroll': !isModalPreview })}>
          <Row>
            <GroupedInsightCard
              insights={insightsData?.pricing_analysis || {}}
              className="pricing-analysis"
              icon="/icons/ai-insights-pricing.svg"
              title="Pricing Insights"
            />
            <GroupedInsightCard
              insights={insightsData?.fee_analysis || {}}
              className="fee-analysis"
              icon="/icons/ai-insights-fee.svg"
              title="Fee Insights"
            />
          </Row>
          <Row>
            <InsightCard
              insights={insightsData?.care_analysis || []}
              className="care-analysis"
              icon="/icons/ai-insights-care.svg"
              title="Care Insights"
            />
            <InsightCard
              insights={insightsData?.incentive_analysis || []}
              className="incentive-analysis"
              icon="/icons/ai-insights-incentive.svg"
              title="Incentive Insights"
            />
          </Row>
        </CardsContainer>
      )}
    </Container>
  );
};

export default AIInsightsWidgetContent;
